import { useState, useCallback, useRef } from "react";
import { t } from "i18n";

/**
 * Hook for executing serial command-response protocols.
 *
 * Registers its own temporary onData listener to buffer lines between
 * start/end markers, independent of the useSerialPort hook's data accumulation.
 *
 * @returns {{ executeCommand: function, isExecuting: boolean, linesReceived: number, cancel: function }}
 */
export function useSerialCommand() {
  const [isExecuting, setIsExecuting] = useState(false);
  const [linesReceived, setLinesReceived] = useState(0);
  const cancelRef = useRef(null);

  const executeCommand = useCallback(
    ({
      command,
      startMarker,
      endMarker,
      timeout = 15000,
      includeStartMarker = false,
      rejectOn = null,
      maxWaitForStart = null,
      kickIfSilentMs = 2500,
    }) => {
      const api = window.electronAPI?.serial;
      if (!api)
        return Promise.reject(
          new Error(t("utils.serialCommand.apiUnavailable"))
        );

      if (isExecuting) {
        return Promise.reject(
          new Error(t("utils.serialCommand.alreadyExecuting"))
        );
      }

      return new Promise((resolve, reject) => {
        setIsExecuting(true);
        setLinesReceived(0);

        const buffer = [];
        let capturing = false;
        let removeDataListener = null;
        let removeDisconnectListener = null;
        let timeoutId = null;
        let startGuardId = null;
        let kickTimerId = null;

        const clearStartGuard = () => {
          if (startGuardId) {
            clearTimeout(startGuardId);
            startGuardId = null;
          }
        };

        const clearKickTimer = () => {
          if (kickTimerId) {
            clearTimeout(kickTimerId);
            kickTimerId = null;
          }
        };

        const cleanup = () => {
          if (removeDataListener) removeDataListener();
          if (removeDisconnectListener) removeDisconnectListener();
          if (timeoutId) clearTimeout(timeoutId);
          clearStartGuard();
          clearKickTimer();
          cancelRef.current = null;
          setIsExecuting(false);
        };

        // Idle timeout: only fires after `timeout` ms with NO new data.
        // A large dump can stream for far longer than `timeout`, so a flat
        // cap measured from command send would truncate it mid-stream.
        const armTimeout = () => {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            // If we were mid-capture, keep what arrived rather than discarding
            // the whole dump — the device just went quiet before the end marker.
            if (capturing && buffer.length > 0) {
              finish({ success: true, lines: buffer, timedOut: true });
            } else {
              const err = new Error(
                `Command timed out after ${Math.round(
                  timeout / 1000
                )}s — no response received`
              );
              err.code = "NO_RESPONSE";
              fail(err);
            }
          }, timeout);
        };

        const finish = (result) => {
          cleanup();
          resolve(result);
        };

        const fail = (err) => {
          cleanup();
          reject(err);
        };

        // Allow external cancellation
        cancelRef.current = () => {
          fail(new Error(t("utils.serialCommand.cancelled")));
        };

        // Listen for disconnect
        removeDisconnectListener = api.onDisconnect(() => {
          fail(new Error(t("utils.serialCommand.disconnected")));
        });

        // Listen for data lines
        removeDataListener = api.onData((data) => {
          // Any traffic at all means the device is still talking — keep waiting.
          armTimeout();

          const line =
            typeof data === "string" ? data.trim() : String(data).trim();
          if (!line) return;

          // Abort early on a known failure line (e.g. "[export] refused …"),
          // even before the start marker — surface a helpful message instead
          // of waiting for an idle timeout.
          if (rejectOn) {
            for (const rule of rejectOn) {
              const hit =
                rule.pattern instanceof RegExp
                  ? rule.pattern.test(line)
                  : line.includes(rule.pattern);
              if (hit) {
                const err = new Error(
                  rule.message || t("utils.serialCommand.rejected", { line })
                );
                err.rejected = true;
                if (rule.code) err.code = rule.code;
                fail(err);
                return;
              }
            }
          }

          // Edge case: battery "No battery history available" before start marker
          if (!capturing && line.includes("No battery history available")) {
            finish({ success: true, lines: ["No battery history available"] });
            return;
          }

          if (!capturing) {
            // Check if this line contains the start marker
            if (typeof startMarker === "string" && line.includes(startMarker)) {
              capturing = true;
              // Real response has begun — the pre-start guards no longer apply.
              clearStartGuard();
              clearKickTimer();
              // Optionally keep the start-marker line — the export banner
              // carries n=/fw=/mac= that the parser needs.
              if (includeStartMarker) {
                buffer.push(line);
                setLinesReceived((prev) => prev + 1);
              }
              // Single-line command (no end marker): the start-marker line IS
              // the whole response (e.g. "[phy] session -> …"). Finish now.
              if (endMarker == null) {
                finish({
                  success: true,
                  lines: buffer.length ? buffer : [line],
                });
                return;
              }
            }
          } else {
            // Check for end marker
            let isEnd = false;
            if (endMarker instanceof RegExp) {
              isEnd = endMarker.test(line);
            } else if (typeof endMarker === "string") {
              isEnd = line.includes(endMarker);
            }

            if (isEnd) {
              finish({ success: true, lines: buffer });
            } else {
              buffer.push(line);
              setLinesReceived((prev) => prev + 1);
            }
          }
        });

        // Arm the initial idle timeout; each incoming line re-arms it.
        armTimeout();

        // Guard against a device that chatters unsolicited logs but never
        // answers the command (e.g. `export` while a session is running, or a
        // wrong line-ending). Background traffic re-arms the idle timeout
        // forever, so without this the command would hang. Cleared the moment
        // the real response (start marker) begins.
        if (maxWaitForStart) {
          startGuardId = setTimeout(() => {
            if (!capturing) {
              const err = new Error(
                `No response to “${command}” after ${Math.round(
                  maxWaitForStart / 1000
                )}s. If a session is running, press End Session first, then retry.`
              );
              err.code = "NO_START";
              fail(err);
            }
          }, maxWaitForStart);
        }

        // Deaf-console recovery. The wearable's console only reads input
        // within a session established by a DTR edge, and certain commands
        // (e.g. `phy` — it restarts the radio) can silently end that session.
        // When deaf, our bytes still land in the device's FIFO — a DTR kick
        // makes the console re-attach, drain the FIFO, and execute them. So:
        // if the response hasn't started shortly after send, kick. Safe by
        // construction — a live console answers well inside this window, and
        // kicking must only ever happen AFTER the bytes are queued (a kick
        // racing an in-flight write once split "export" into "ex").
        if (kickIfSilentMs && api.kick) {
          kickTimerId = setTimeout(() => {
            if (!capturing) api.kick().catch(() => {});
          }, kickIfSilentMs);
        }

        // Send the command byte
        api
          .send(command)
          .catch((err) =>
            fail(
              new Error(
                t("utils.serialCommand.sendFailed", { message: err.message })
              )
            )
          );
      });
    },
    [isExecuting]
  );

  const cancel = useCallback(() => {
    if (cancelRef.current) cancelRef.current();
  }, []);

  return { executeCommand, isExecuting, linesReceived, cancel };
}
